
// Boards v2's list lives in its own Vue 3 bundle (web/board/src/list, built as
// dist/boards.js). This page is only the seam: it loads that bundle lazily and
// mounts it into the app's normal frame, hands it the account being acted as,
// the caller's permissions, the company's labels and this app's Firebase and
// API. The list has its own Edit and Access Settings (IL-054). It mounts only
// for an account that has Boards v2, and sends one without it (signed in as,
// or switched to) to the classic list.
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { CompanyCustomFieldDef } from '@run-diffusion/shared';
import { auth, db, FieldPath, FieldValue, functions, storage } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { BoardsV2AccessMixin, TeamUserMixin } from '@/mixins';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { BOARDS_API_BASE_URL } from '@/utils/boardsApi';
import { rememberBoardsVersion } from '@/utils/boardsVersion';
import { boardsTourSeen, tourBeforeFirstBoard } from '@/utils/boardsTour';
import { BOARDS_DEV_BACKEND } from '@/utils/boardsDevBackend';
import { forgetWebClientDeviceIfRejected, internalApiCredentials } from '@/services/internalApiCredentials';
import BaseButton from '@/components/base/BaseButton.vue';
import type { BoardsListAccount, BoardsListLabels, BoardsListNotice, BoardsListPermissions, BoardsListUser, MountedBoardsList } from '@boards-app';

const NOTICE_STATUS: Record<BoardsListNotice['status'], string> = {
	success: SNACKBAR_STATUS.SUCCESS,
	error: SNACKBAR_STATUS.ERROR,
	info: SNACKBAR_STATUS.INFO,
};

// Development only: `npm run dev` rebuilds the list bundle as you edit it; remount in place.
let hotRemount: (() => void) | null = null;
if (process.env.NODE_ENV !== 'production' && module.hot) {
	module.hot.accept('@boards-app', () => {
		if (hotRemount) hotRemount();
	});
}

export default Vue.extend({
	name: 'BoardsListPage',
	metaInfo: {
		title: 'Boards',
	},
	mixins: [
		TeamUserMixin,
		BoardsV2AccessMixin,
	],
	data () {
		return {
			list: null as MountedBoardsList | null,
			mountStarted: false,
			loadError: null as string | null,
			destroyed: false,
			stickyTop: 0,
			boardPrefetched: false,
		};
	},
	computed: {
		...mapState([
			'user',
			'authUser',
			'team',
			'runnitState',
			'toolbar',
		]),
		...mapGetters([
			'displayLabels',
		]),
		isTeamMode (): boolean {
			return this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM;
		},
		listUser (): BoardsListUser {
			return {
				uid: this.authUser ? this.authUser.uid : (this.user && this.user.id),
				email: (this.user && this.user.email) || (this.authUser && this.authUser.email) || '',
				name: (this.authUser && this.authUser.displayName) || '',
				isAdmin: !!(this.user && this.user.isAdmin),
			};
		},
		listAccount (): BoardsListAccount {
			const team = this.isTeamMode ? this.team : null;
			const defs: CompanyCustomFieldDef[] = (team && team.company && team.company.runnitCustomFieldDefs) || [];
			return {
				teamId: team ? team.id : null,
				teamName: team ? team.name : null,
				companyId: team ? team.companyId || null : null,
				customFields: defs.map((def: CompanyCustomFieldDef) => ({ key: def.key, label: def.label })),
			};
		},
		listPermissions (): BoardsListPermissions {
			return {
				manageAllGenerations: !!this.hasManageAllGenerationsPermission,
				shareRunnitAssets: !!this.hasShareRunnitAssetsPermission,
			};
		},
		listLabels (): BoardsListLabels {
			return { team: this.displayLabels.team };
		},
	},
	watch: {
		boardsV2Access () {
			this.applyAccess();
		},
		listUser (next: BoardsListUser) {
			if (this.list) this.list.update({ user: next });
		},
		listAccount (next: BoardsListAccount, prev: BoardsListAccount) {
			if (this.list && JSON.stringify(next) !== JSON.stringify(prev)) this.list.update({ account: next });
		},
		listPermissions (next: BoardsListPermissions, prev: BoardsListPermissions) {
			if (this.list && JSON.stringify(next) !== JSON.stringify(prev)) this.list.update({ permissions: next });
		},
		listLabels (next: BoardsListLabels) {
			if (this.list) this.list.update({ labels: next });
		},
		'toolbar.runnitsSideNavOpen' () {
			this.$nextTick(this.measureStickyTop);
		},
	},
	mounted () {
		hotRemount = () => this.reload();
		window.addEventListener('resize', this.measureStickyTop);
		this.measureStickyTop();
		this.applyAccess();
	},
	beforeDestroy () {
		this.destroyed = true;
		hotRemount = null;
		window.removeEventListener('resize', this.measureStickyTop);
		this.teardown();
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		// Waits while the account loads; mounts the list once it has Boards v2
		applyAccess () {
			if (this.destroyed) return;
			if (this.boardsV2Access === 'off') {
				this.leaveBoardsV2();
				return;
			}
			if (this.boardsV2Access === 'on' && !this.mountStarted) {
				rememberBoardsVersion('v2');
				this.mount();
			}
		},
		async mount () {
			this.mountStarted = true;
			this.loadError = null;
			try {
				const mod = await import(/* webpackChunkName: "boards-list" */ '@boards-app');
				if (this.destroyed) return;
				this.list = mod.mountBoardsList(this.$refs.host as HTMLElement, {
					user: this.listUser,
					account: this.listAccount,
					permissions: this.listPermissions,
					labels: this.listLabels,
					firebase: { db, auth, functions, storage, FieldValue, FieldPath },
					api: {
						baseUrl: BOARDS_API_BASE_URL,
						credentials: internalApiCredentials,
						onRejected: forgetWebClientDeviceIfRejected,
					},
					stickyTop: this.stickyTop,
					openBoard: this.openBoard,
					openClassic: this.openClassic,
					// the hero's Take the tour (IL-095)
					openTour: this.openTour,
					notify: this.notify,
					prefetchBoard: this.prefetchBoard,
					// admins start one under New board; a local build's Dev chip, anyone
					createDemoBoard: this.createDemoBoard,
					// the Dev chip (local development builds only; a production list leaves it out)
					openStress: this.openStress,
					backend: BOARDS_DEV_BACKEND,
				});
			} catch (e) {
				console.error('[boards] failed to mount the list', e);
				this.loadError = (e && (e as Error).message) || 'Unknown error';
			}
		},
		teardown () {
			this.mountStarted = false;
			if (!this.list) return;
			try {
				this.list.unmount();
			} catch (e) {
				console.error('[boards] failed to unmount the list cleanly', e);
			}
			this.list = null;
		},
		reload () {
			this.teardown();
			this.applyAccess();
		},
		// The toolbar docks under the app's own fixed bar, which App.vue sizes through v-main's padding
		measureStickyTop () {
			const main: HTMLElement | null = this.$el ? (this.$el as HTMLElement).closest('.v-main') : null;
			const top: number = main ? parseFloat(getComputedStyle(main).paddingTop) || 0 : 0;
			if (top === this.stickyTop) return;
			this.stickyTop = top;
			if (this.list) this.list.update({ stickyTop: top });
		},
		// A board just made here that's the person's first Boards v2 board, by
		// someone who's never seen the tour, gets the tour first (IL-095); it ends
		// by opening the board.
		async openBoard (id: string, how?: { created?: boolean }) {
			if (how && how.created && !boardsTourSeen(this.user) && await this.firstBoard()) {
				this.$router.push({ name: ROUTER.BOARDS_TOUR, query: { reason: 'first-board', board: id } }).catch(() => {});
				return;
			}
			this.$router.push({ name: ROUTER.BOARD, params: { boardId: id } }).catch(() => {});
		},
		/** The person's canvas boards in this account: the one just made, and no other. */
		async firstBoard (): Promise<boolean> {
			const uid: string | null = this.authUser ? this.authUser.uid : (this.user && this.user.id);
			if (!uid) return false;
			try {
				const snap = await db.collection('runnits')
					.where('userId', '==', uid)
					.where('teamId', '==', this.listAccount.teamId || null)
					.where('type', '==', 'CANVAS')
					.limit(2)
					.get();
				return tourBeforeFirstBoard(this.user, snap.size);
			} catch (e) {
				// can't tell: the board opens, as it always has
				console.error('[boards] could not count boards for the tour', e);
				return false;
			}
		},
		openTour () {
			this.$router.push({ name: ROUTER.BOARDS_TOUR, query: { reason: 'replay', back: this.$route.fullPath } }).catch(() => {});
		},
		openStress (images?: number) {
			this.$router.push({ name: ROUTER.BOARD_STRESS, query: images ? { images: String(images) } : {} }).catch(() => {});
		},
		openClassic () {
			rememberBoardsVersion('v1');
			this.$router.push({ name: ROUTER.RUNNIT_BOARDS_PAGE }).catch(() => {});
		},
		notify (notice: BoardsListNotice) {
			this.updateSnackbar({
				status: NOTICE_STATUS[notice.status] || SNACKBAR_STATUS.INFO,
				message: notice.message,
				show: true,
			});
		},
		// Hovering a board starts loading the board app, so opening one is quick
		prefetchBoard () {
			if (this.boardPrefetched) return;
			this.boardPrefetched = true;
			import(/* webpackChunkName: "board-app" */ '@board-app').catch(() => {
				this.boardPrefetched = false;
			});
		},
		// your demo board opens again; `fresh` makes another (IL-100)
		async createDemoBoard (opts?: { fresh?: boolean }): Promise<string | null> {
			const mod = await import(/* webpackChunkName: "board-app" */ '@board-app');
			return mod.createDemoBoard(
				{ db, auth, functions, storage, FieldValue, FieldPath },
				{
					uid: this.listUser.uid,
					name: this.listUser.name,
					email: this.listUser.email,
					isAdmin: this.listUser.isAdmin,
					teamId: this.listAccount.teamId,
				},
				opts,
			);
		},
	},
	components: {
		BaseButton,
	},
});
